/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * auth-redirect.ts
 *
 * Issue 1 (FCR Pass 10): Centralises the auth login redirect so the six
 * call sites (cart.tsx, list.tsx, upgrade-checkout.tsx, layouts/dashboard.tsx,
 * http-client.ts, api-config.ts) share one implementation. Benefits:
 *
 *  - Clears any stale token before redirecting (prevents previous account
 *    session leaking across users on shared devices).
 *  - Validates the returnTo URL is same-origin (blocks open-redirect attacks
 *    where a crafted ?redirect= value could send the user off-origin after login).
 *  - Configurable login path: defaults to /shop/ today; flips to /auth at
 *    Wave C cutover by setting VITE_AUTH_LOGIN_PATH=/auth.
 *
 * Wave C note: the base URL and path are already configurable via
 * VITE_AUTH_FRONTEND_URL / VITE_AUTH_LOGIN_PATH. No code change is needed at
 * cutover -- only environment variables change.
 */

import configuration from "@/config/configuration";
import { clearAuthToken } from "@/lib/token-cache";
import { logger } from "@/lib/logger";

/**
 * Validate that a returnTo URL is same-origin as the current page.
 * Returns the safe URL string if valid, undefined otherwise.
 */
function safeReturnTo(returnTo?: string): string | undefined {
  if (!returnTo) return undefined;
  try {
    const u = new URL(returnTo, window.location.origin);
    return u.origin === window.location.origin ? u.href : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Build the auth login URL with an optional returnTo redirect.
 *
 * Uses VITE_AUTH_FRONTEND_URL if set, falling back to
 * VITE_BETTER_AUTH_FRONTEND_URL so no env change is required today.
 * Login path defaults to "/shop/" and flips to the configured value at
 * Wave C cutover.
 */
export function buildAuthLoginUrl(returnTo?: string): string {
  const base = (
    configuration.auth_frontend_url ||
    configuration.better_auth_frontend_url ||
    "https://deskshare.com"
  ).replace(/\/$/, "");

  // auth_login_path flips from /shop/ to /auth at Wave C via VITE_AUTH_LOGIN_PATH
  const path = configuration.auth_login_path || "/shop/";
  const safe = safeReturnTo(returnTo);

  return safe
    ? `${base}${path}?redirect=${encodeURIComponent(safe)}`
    : `${base}${path}`;
}

/**
 * Clear the stored auth token and redirect to the auth login page.
 *
 * Call this from every place that currently does:
 *   window.location.href = `${baseUrl}/shop/?redirect=...`
 *
 * Clearing the token before redirecting prevents a previous account's
 * session from leaking across users on shared devices.
 */
export function redirectToAuthLogin(returnTo?: string): void {
  clearAuthToken();
  const url = buildAuthLoginUrl(returnTo);
  logger.info("Redirecting to auth login", { url });
  window.location.href = url;
}
